import { computed, Ref, ref } from 'vue'
import { defineStore } from 'pinia'
import { CompaniesResponse, getAllCompanies } from '@cognitivecredit/ui-utils'
import { AxiosResponse } from 'axios'

// This store contains (for now) only the list of all companies as
// returned by the getAllCompanies API call, which returns only
// id, name, ticker, and licenseIds for each company.
// This is used in various places in the app where we need to map
// company IDs to names or tickers without fetching full company details.

export const useCompaniesStore = defineStore('companies', () => {
  const companiesLoaded = ref(false)
  const loadingCompanies: Ref<Promise<AxiosResponse<Record<string, CompaniesResponse>, any>> | null> = ref(null)
  const companies: Ref<Record<string, CompaniesResponse>> = ref({})

  const loadCompanies = async () => {
    try {
      // loadCompanies may be called multiple times in quick succession (e.g. if the user navigates to DealConnect, this will also be called in App.vue and the beforeEach hook), so we want to avoid making multiple API calls. If a call is already in progress, we can just await it.
      if (!loadingCompanies.value) {
        loadingCompanies.value = getAllCompanies() as Promise<AxiosResponse<Record<string, CompaniesResponse>, any>>
      }
      const res = await loadingCompanies.value
      companies.value = res!.data
      companiesLoaded.value = true
    } catch (e) {
      console.error('`[ERROR] Loading companies failed:', e)
    } finally {
      loadingCompanies.value = null
    }
    return companies.value
  }
  const getCompanyById = (companyId: string) => {
    if (!companiesLoaded.value) {
      throw new Error('Companies not loaded yet. Call loadCompanies() first.')
    }
    return companies.value[companyId]
  }
  const companyHasDealDocs = (companyId: string) => {
    const company = getCompanyById(companyId)
    return company ? company.hasDealDocs : false
  }
  const clientHasAnyDealDocs = computed(() => {
    if (!companiesLoaded.value) {
      return false
    }
    return Object.values(companies.value).some((company) => company.hasDealDocs)
  })
  return {
    loadCompanies,
    companiesLoaded,
    loadingCompanies,
    companies,
    getCompanyById,
    companyHasDealDocs,
    clientHasAnyDealDocs,
  }
})
